(() => {
  const initializeMobileMediaFlow = () => {
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('.product:not([data-mobile-media-flow-ready])').forEach((product) => {
      const media = product.querySelector('.product__media');
      const primary = media?.querySelector('.product__media-primary');
      const track = media?.querySelector('[data-product-media-track]');
      const purchase = product.querySelector('.product__purchase');

      if (!media || !primary || !track || !purchase) return;

      product.dataset.mobileMediaFlowReady = 'true';
      const primaryPosition = document.createComment('product-primary-media-position');
      primary.before(primaryPosition);

      const sync = () => {
        if (mobileQuery.matches) {
          if (primary.parentElement !== track) track.prepend(primary);
          primary.classList.add('product__media-item');
          primary.style.order = '0';
          track.style.order = '1';
          purchase.style.order = '2';
          return;
        }

        if (primary.parentElement === track) primaryPosition.after(primary);
        primary.classList.remove('product__media-item');
        primary.style.removeProperty('order');
        track.style.removeProperty('order');
        purchase.style.removeProperty('order');
      };

      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', sync);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(sync);
      }

      sync();
    });
  };

  const initializeGallery = () => {
    document.querySelectorAll('[data-product-media-gallery]:not([data-product-media-ready])').forEach((gallery) => {
      const track = gallery.querySelector('[data-product-media-track]');
      const previous = gallery.querySelector('[data-product-media-previous]');
      const next = gallery.querySelector('[data-product-media-next]');
      if (!track || !previous || !next) return;

      gallery.dataset.productMediaReady = 'true';
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const supportsScrollEnd = 'onscrollend' in track;
      let isRepositioning = false;
      let scrollSettleTimer;

      const step = () => {
        const item = track.querySelector('.product__media-item:not([hidden])');
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      };
      const move = (direction) => track.scrollBy({ left: direction * step(), behavior: 'smooth' });

      const removeLegacyClones = () => {
        track.querySelectorAll('[data-product-media-clone]').forEach((clone) => clone.remove());
      };

      const scrollToItem = (item) => {
        if (!item) return;
        const styles = getComputedStyle(track);
        const scrollPadding =
          Number.parseFloat(styles.scrollPaddingInlineStart) || Number.parseFloat(styles.paddingInlineStart) || 0;
        const trackRect = track.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const itemLeft = track.scrollLeft + itemRect.left - trackRect.left;
        track.scrollTo({ left: Math.max(0, itemLeft - scrollPadding), behavior: 'auto' });
      };

      const visibleItems = () => Array.from(track.querySelectorAll('.product__media-item:not([hidden])'));
      const closestVisibleItem = () => {
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        return visibleItems().reduce((closest, item) => {
          const itemRect = item.getBoundingClientRect();
          const distance = Math.abs(itemRect.left + itemRect.width / 2 - trackCenter);
          return !closest || distance < closest.distance ? { distance, item } : closest;
        }, null)?.item;
      };

      const centerCircularItem = (currentItem, force = false) => {
        const items = visibleItems();
        const currentIndex = items.indexOf(currentItem);
        if (items.length < 2 || currentIndex < 0) return;

        if (currentIndex !== 1) {
          const firstIndex = (currentIndex - 1 + items.length) % items.length;
          const circularItems = Array.from(
            { length: items.length },
            (_, offset) => items[(firstIndex + offset) % items.length]
          );
          circularItems.forEach((item) => track.append(item));
        } else if (!force) {
          return;
        }

        isRepositioning = true;
        requestAnimationFrame(() => {
          scrollToItem(currentItem);
          update();
          requestAnimationFrame(() => {
            isRepositioning = false;
          });
        });
      };

      const rebuildCircularScroll = () => {
        removeLegacyClones();

        if (!mobileQuery.matches) {
          track.scrollTo({ left: 0, behavior: 'auto' });
          requestAnimationFrame(update);
          return;
        }

        const items = visibleItems();
        const primaryItem = items.find((item) => item.classList.contains('product__media-primary')) || items[0];
        if (!primaryItem) return;
        centerCircularItem(primaryItem, true);
      };

      const normalizeCircularScroll = () => {
        if (!mobileQuery.matches || isRepositioning) return;

        const currentItem = closestVisibleItem();
        if (currentItem) centerCircularItem(currentItem);
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener(
        'scroll',
        () => {
          requestAnimationFrame(update);
          if (!mobileQuery.matches || isRepositioning || supportsScrollEnd) return;
          window.clearTimeout(scrollSettleTimer);
          scrollSettleTimer = window.setTimeout(normalizeCircularScroll, 180);
        },
        { passive: true }
      );
      if (supportsScrollEnd) track.addEventListener('scrollend', normalizeCircularScroll);
      gallery.addEventListener('product:media-filter', () => {
        requestAnimationFrame(rebuildCircularScroll);
      });

      const syncCircularMode = () => requestAnimationFrame(rebuildCircularScroll);
      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', syncCircularMode);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(syncCircularMode);
      }

      update();
    });
  };

  const initializeLightbox = () => {
    document.querySelectorAll('.product:not([data-product-lightbox-ready])').forEach((product) => {
      const dialog = product.querySelector('[data-product-lightbox]');
      const image = product.querySelector('[data-product-lightbox-image]');
      const close = product.querySelector('[data-product-lightbox-close]');
      const previous = product.querySelector('[data-product-lightbox-previous]');
      const next = product.querySelector('[data-product-lightbox-next]');
      const zoom = product.querySelector('[data-product-lightbox-zoom]');
      const counter = product.querySelector('[data-product-lightbox-counter]');
      const triggers = Array.from(product.querySelectorAll('[data-product-media-open]'));

      if (!dialog || !image || !close || !previous || !next || !zoom || triggers.length === 0) return;

      product.dataset.productLightboxReady = 'true';
      let currentIndex = 0;

      const activeTriggers = () =>
        Array.from(product.querySelectorAll('[data-product-media-open]')).filter((trigger) => {
          const mediaItem = trigger.closest('.product__media-item');
          return !mediaItem || !mediaItem.hidden;
        });

      const setZoomed = (isZoomed) => {
        image.classList.toggle('is-zoomed', isZoomed);
        zoom.textContent = isZoomed ? 'Fit' : 'Zoom';
        zoom.setAttribute('aria-pressed', String(isZoomed));
      };

      const render = () => {
        const visibleTriggers = activeTriggers();
        if (visibleTriggers.length === 0) return;

        currentIndex = Math.min(currentIndex, visibleTriggers.length - 1);
        const trigger = visibleTriggers[currentIndex];
        image.src = trigger.dataset.productMediaSrc;
        image.alt = trigger.dataset.productMediaAlt || '';
        setZoomed(false);

        const hasMultiple = visibleTriggers.length > 1;
        previous.hidden = !hasMultiple;
        next.hidden = !hasMultiple;
        if (counter) counter.textContent = `${currentIndex + 1} / ${visibleTriggers.length}`;
      };

      const openAt = (trigger) => {
        const visibleTriggers = activeTriggers();
        const triggerIndex = visibleTriggers.indexOf(trigger);
        currentIndex = triggerIndex >= 0 ? triggerIndex : 0;
        render();
        dialog.showModal();
        document.body.classList.add('product-lightbox-open');
        close.focus();
      };

      const move = (direction) => {
        const visibleTriggers = activeTriggers();
        if (visibleTriggers.length === 0) return;
        currentIndex = (currentIndex + direction + visibleTriggers.length) % visibleTriggers.length;
        render();
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => openAt(trigger));
      });

      close.addEventListener('click', () => dialog.close());
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      zoom.addEventListener('click', () => setZoomed(!image.classList.contains('is-zoomed')));
      image.addEventListener('dblclick', () => setZoomed(!image.classList.contains('is-zoomed')));

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' && activeTriggers().length > 1) {
          event.preventDefault();
          move(-1);
        }
        if (event.key === 'ArrowRight' && activeTriggers().length > 1) {
          event.preventDefault();
          move(1);
        }
      });

      dialog.addEventListener('close', () => {
        document.body.classList.remove('product-lightbox-open');
        setZoomed(false);
        activeTriggers()[currentIndex]?.focus();
      });
    });
  };

  const installVariantPickerStyles = () => {
    if (document.querySelector('[data-product-variant-picker-styles]')) return;

    const style = document.createElement('style');
    style.dataset.productVariantPickerStyles = 'true';
    style.textContent = `
      .product-variant-select--enhanced {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }

      .product-variant-picker {
        display: grid;
        gap: 1.25rem;
      }

      .product-variant-picker__group {
        display: grid;
        gap: 0.7rem;
      }

      .product-variant-picker__header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 1rem;
      }

      .product-variant-picker__label {
        font-weight: 700;
      }

      .product-variant-picker__guide {
        padding: 0;
        border: 0;
        border-block-end: 1px solid currentColor;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font: inherit;
        font-size: 0.9rem;
        font-weight: 700;
      }

      .product-variant-picker__guide:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 3px;
      }

      .product-variant-picker__options {
        display: flex;
        flex-wrap: wrap;
        gap: 0.55rem;
      }

      .product-variant-picker__option {
        display: inline-grid;
        place-items: center;
        min-width: 3.25rem;
        min-height: 3rem;
        padding: 0.55rem 0.8rem;
        border: 1px solid var(--color-border);
        border-radius: 0.45rem;
        background: var(--color-background);
        color: var(--color-foreground);
        cursor: pointer;
        font: inherit;
        font-weight: 700;
        line-height: 1;
      }

      .product-variant-picker__option:hover:not(:disabled) {
        border-color: currentColor;
      }

      .product-variant-picker__option[aria-checked='true'] {
        border-color: var(--color-foreground);
        background: var(--color-foreground);
        color: var(--color-background);
      }

      .product-variant-picker__option:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 3px;
      }

      .product-variant-picker__option:disabled {
        opacity: 0.42;
        cursor: not-allowed;
        text-decoration: line-through;
      }

      .product-variant-picker__option--color {
        position: relative;
        width: 2.55rem;
        min-width: 2.55rem;
        height: 2.55rem;
        min-height: 2.55rem;
        padding: 0.25rem;
        border-radius: 999px;
        background: var(--color-background);
      }

      .product-variant-picker__option--color::before {
        position: absolute;
        inset: 0.25rem;
        border: 1px solid color-mix(in srgb, var(--color-foreground) 18%, transparent);
        border-radius: inherit;
        background: var(--swatch-background, #a8aaad);
        content: '';
      }

      .product-variant-picker__option--color:hover:not(:disabled) {
        border-color: var(--color-foreground);
      }

      .product-variant-picker__option--color[aria-checked='true'] {
        border-color: var(--color-foreground);
        background: var(--color-background);
        color: var(--color-foreground);
        box-shadow:
          0 0 0 2px var(--color-background),
          0 0 0 4px var(--color-foreground);
      }
    `;
    document.head.append(style);
  };

  const initializeVariantPickers = () => {
    document.querySelectorAll('.product:not([data-product-variant-picker-ready])').forEach((product) => {
      const form = product.querySelector('.product-form');
      const select = form?.querySelector('select[name="id"]');
      if (!form || !select || select.options.length === 0) return;

      let optionNames;
      let variants;

      try {
        optionNames = JSON.parse(select.dataset.productOptionNames || '[]');
        variants = Array.from(select.options).map((option) => ({
          available: !option.disabled,
          id: option.value,
          mediaId: option.dataset.variantMediaId || '',
          price: option.dataset.variantPrice || '',
          values: JSON.parse(option.dataset.variantOptions || '[]'),
        }));
      } catch (error) {
        return;
      }

      const hasCompleteOptionData =
        Array.isArray(optionNames) &&
        optionNames.length > 0 &&
        variants.every((variant) => Array.isArray(variant.values) && variant.values.length === optionNames.length);

      if (!hasCompleteOptionData) return;

      product.dataset.productVariantPickerReady = 'true';
      installVariantPickerStyles();

      const variantLabel = product.querySelector(`label[for="${CSS.escape(select.id)}"]`);
      if (variantLabel) variantLabel.hidden = true;
      select.classList.add('product-variant-select--enhanced');

      const picker = document.createElement('div');
      picker.className = 'product-variant-picker';

      const fitDisclosure = Array.from(product.querySelectorAll('.product__disclosure')).find((disclosure) => {
        const summary = disclosure.querySelector('summary');
        return summary?.textContent.trim().toLowerCase() === 'fit & size';
      });

      let selectedVariant = variants.find((variant) => variant.id === select.value) || variants.find((variant) => variant.available) || variants[0];
      let selectedValues = [...selectedVariant.values];
      let currentMediaIndex = 0;
      const mediaTriggers = Array.from(product.querySelectorAll('[data-product-media-open]'));
      const mediaIdsInOrder = mediaTriggers.map((trigger) => trigger.dataset.productMediaId).filter(Boolean);
      const mediaMetadata = new Map(
        mediaTriggers
          .map((trigger) => {
            const mediaId = trigger.dataset.productMediaId;
            const source = trigger.dataset.productMediaSrc || '';
            const filename = decodeURIComponent(source.split('?')[0].split('/').pop() || '');
            const viewMatch = filename.match(/^(.*)-(front|left|right|back)-.+\.[^.]+$/i);

            return [
              mediaId,
              {
                groupKey: viewMatch?.[1] || '',
                view: viewMatch?.[2]?.toLowerCase() || '',
              },
            ];
          })
          .filter(([mediaId]) => Boolean(mediaId))
      );
      const sizeOptionIndexes = new Set(
        optionNames
          .map((optionName, optionIndex) => (optionName.toLowerCase().includes('size') ? optionIndex : -1))
          .filter((optionIndex) => optionIndex >= 0)
      );
      const mediaGroupKeyForVariant = (variant) =>
        variant.values
          .filter((value, optionIndex) => !sizeOptionIndexes.has(optionIndex))
          .map((value) => value.trim().toLowerCase())
          .join('\u001f');
      const mediaIdCountsByGroup = new Map();

      variants.forEach((variant) => {
        if (!variant.mediaId) return;
        const groupKey = mediaGroupKeyForVariant(variant);
        const mediaIdCounts = mediaIdCountsByGroup.get(groupKey) || new Map();
        mediaIdCounts.set(variant.mediaId, (mediaIdCounts.get(variant.mediaId) || 0) + 1);
        mediaIdCountsByGroup.set(groupKey, mediaIdCounts);
      });

      const representativeMediaIds = new Map(
        [...mediaIdCountsByGroup.entries()].map(([groupKey, mediaIdCounts]) => [
          groupKey,
          [...mediaIdCounts.entries()].sort((first, second) => second[1] - first[1])[0]?.[0] || '',
        ])
      );
      const resolvedMediaIdForVariant = (variant) =>
        representativeMediaIds.get(mediaGroupKeyForVariant(variant)) ||
        variant.mediaId ||
        mediaIdsInOrder[0] ||
        '';
      const featuredMediaIds = [...new Set(variants.map(resolvedMediaIdForVariant).filter(Boolean))];
      const mediaAnchors = featuredMediaIds
        .map((mediaId) => ({ mediaId, index: mediaIdsInOrder.indexOf(mediaId) }))
        .filter((anchor) => anchor.index >= 0)
        .sort((first, second) => first.index - second.index);
      const mediaSpans = mediaAnchors
        .slice(0, -1)
        .map((anchor, index) => mediaAnchors[index + 1].index - anchor.index)
        .filter((span) => span > 0);
      const spanCounts = mediaSpans.reduce((counts, span) => {
        counts.set(span, (counts.get(span) || 0) + 1);
        return counts;
      }, new Map());
      const fallbackMediaSpan =
        [...spanCounts.entries()].sort((first, second) => second[1] - first[1] || first[0] - second[0])[0]?.[0] || 1;
      const mediaGroups = new Map();

      mediaAnchors.forEach((anchor, index) => {
        const anchorMetadata = mediaMetadata.get(anchor.mediaId);
        const filenameGroup = anchorMetadata?.groupKey
          ? mediaIdsInOrder.filter((mediaId) => mediaMetadata.get(mediaId)?.groupKey === anchorMetadata.groupKey)
          : [];
        const filenameGroupViews = filenameGroup.map((mediaId) => mediaMetadata.get(mediaId)?.view).filter(Boolean);

        // Printful can append a color's mockups out of sequence or use a
        // different viewpoint order. Prefer its filename hints when they
        // identify one distinct image per viewpoint.
        if (filenameGroup.length > 1 && new Set(filenameGroupViews).size === filenameGroup.length) {
          mediaGroups.set(anchor.mediaId, new Set(filenameGroup));
          return;
        }

        const nextAnchor = mediaAnchors[index + 1];
        const endIndex =
          mediaAnchors.length === 1
            ? mediaIdsInOrder.length
            : nextAnchor?.index ?? Math.min(mediaIdsInOrder.length, anchor.index + fallbackMediaSpan);
        mediaGroups.set(anchor.mediaId, new Set(mediaIdsInOrder.slice(anchor.index, endIndex)));
      });

      const primaryPrevious = product.querySelector('[data-product-primary-previous]');
      const primaryNext = product.querySelector('[data-product-primary-next]');
      const primaryStatus = product.querySelector('[data-product-primary-status]');
      const mediaTrack = product.querySelector('[data-product-media-track]');
      const mobileQuery = window.matchMedia('(max-width: 749px)');

      const mediaIdsForVariant = (variant) => {
        const resolvedMediaId = variant ? resolvedMediaIdForVariant(variant) : '';
        if (!resolvedMediaId) return [];
        const mediaGroup = mediaGroups.get(resolvedMediaId);
        const mediaIds = mediaGroup?.size ? [...mediaGroup] : [resolvedMediaId];
        const viewOrder = new Map([
          ['front', 0],
          ['left', 1],
          ['back', 2],
          ['right', 3],
        ]);
        const mediaViews = mediaIds.map((mediaId) => mediaMetadata.get(mediaId)?.view).filter(Boolean);

        if (mediaViews.length === mediaIds.length && new Set(mediaViews).size === mediaIds.length) {
          return [...mediaIds].sort(
            (first, second) =>
              (viewOrder.get(mediaMetadata.get(first)?.view) ?? Number.MAX_SAFE_INTEGER) -
              (viewOrder.get(mediaMetadata.get(second)?.view) ?? Number.MAX_SAFE_INTEGER)
          );
        }

        // Printful's four standard apparel views arrive as front, left, right, back.
        // Reorder them so the gallery travels around the garment in one direction.
        return mediaIds.length === 4 ? [mediaIds[0], mediaIds[1], mediaIds[3], mediaIds[2]] : mediaIds;
      };

      const updatePrimaryControls = (variant) => {
        const mediaIds = mediaIdsForVariant(variant);
        const hasMultipleViews = mediaIds.length > 1;

        if (primaryPrevious) primaryPrevious.hidden = !hasMultipleViews;
        if (primaryNext) primaryNext.hidden = !hasMultipleViews;
        if (primaryStatus && mediaIds.length > 0) {
          primaryStatus.textContent = `Product view ${currentMediaIndex + 1} of ${mediaIds.length}`;
        }
      };

      const groups = optionNames.map((optionName, optionIndex) => {
        const group = document.createElement('div');
        group.className = 'product-variant-picker__group';
        const isColorOption = /colou?r/i.test(optionName);
        if (isColorOption) group.classList.add('product-variant-picker__group--color');

        const header = document.createElement('div');
        header.className = 'product-variant-picker__header';

        const label = document.createElement('span');
        const labelId = `${select.id}-Option-${optionIndex}-Label`;
        label.id = labelId;
        label.className = 'product-variant-picker__label';
        let selectedValueLabel;
        if (isColorOption) {
          label.append(`${optionName}: `);
          selectedValueLabel = document.createElement('span');
          selectedValueLabel.className = 'product-variant-picker__selected-value';
          label.append(selectedValueLabel);
        } else {
          label.textContent = optionName;
        }
        header.append(label);

        if (fitDisclosure && optionName.toLowerCase().includes('size')) {
          const guide = document.createElement('button');
          guide.type = 'button';
          guide.className = 'product-variant-picker__guide';
          guide.textContent = 'Size Guide';
          guide.addEventListener('click', () => {
            fitDisclosure.open = true;
            fitDisclosure.scrollIntoView({ behavior: 'smooth', block: 'center' });
            window.setTimeout(() => fitDisclosure.querySelector('summary')?.focus(), 350);
          });
          header.append(guide);
        }

        const options = document.createElement('div');
        options.className = 'product-variant-picker__options';
        options.setAttribute('role', 'radiogroup');
        options.setAttribute('aria-labelledby', labelId);

        const optionValues = [...new Set(variants.map((variant) => variant.values[optionIndex]))];
        const buttons = optionValues.map((optionValue) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'product-variant-picker__option';
          button.dataset.optionIndex = String(optionIndex);
          button.dataset.optionValue = optionValue;
          button.setAttribute('role', 'radio');

          if (isColorOption) {
            button.classList.add('product-variant-picker__option--color');
            button.title = optionValue;
            window.FerryColorSwatches?.decorate(button, optionValue);
            const hiddenLabel = document.createElement('span');
            hiddenLabel.className = 'visually-hidden';
            hiddenLabel.textContent = optionValue;
            button.append(hiddenLabel);
          } else {
            button.textContent = optionValue;
          }

          options.append(button);
          return button;
        });

        group.append(header, options);
        picker.append(group);
        return { buttons, optionIndex, selectedValueLabel };
      });

      const availableVariantFor = (optionIndex, optionValue) =>
        variants.find(
          (variant) =>
            variant.available &&
            variant.values[optionIndex] === optionValue &&
            variant.values.slice(0, optionIndex).every((value, index) => value === selectedValues[index])
        );

      const swapAttribute = (first, second, name) => {
        const firstValue = first.getAttribute(name);
        const secondValue = second.getAttribute(name);

        if (secondValue === null) first.removeAttribute(name);
        else first.setAttribute(name, secondValue);

        if (firstValue === null) second.removeAttribute(name);
        else second.setAttribute(name, firstValue);
      };

      const syncVariantMedia = (variant) => {
        const mediaIds = mediaIdsForVariant(variant);
        if (mediaIds.length === 0) {
          updatePrimaryControls(variant);
          return;
        }

        currentMediaIndex = Math.min(currentMediaIndex, mediaIds.length - 1);
        const targetMediaId = mediaIds[currentMediaIndex];

        const primaryTrigger = product.querySelector('.product__media-primary [data-product-media-open]');
        const matchingTrigger = Array.from(product.querySelectorAll('[data-product-media-open]')).find(
          (trigger) => trigger.dataset.productMediaId === targetMediaId
        );

        if (!primaryTrigger || !matchingTrigger) {
          updatePrimaryControls(variant);
          return;
        }

        if (matchingTrigger !== primaryTrigger) {
          const primaryChildren = Array.from(primaryTrigger.childNodes);
          const matchingChildren = Array.from(matchingTrigger.childNodes);
          primaryTrigger.replaceChildren(...matchingChildren);
          matchingTrigger.replaceChildren(...primaryChildren);

          ['aria-label', 'data-product-media-id', 'data-product-media-src', 'data-product-media-alt'].forEach((name) => {
            swapAttribute(primaryTrigger, matchingTrigger, name);
          });

          const primaryImage = primaryTrigger.querySelector('img');
          if (primaryImage) {
            primaryImage.loading = 'eager';
            primaryImage.fetchPriority = 'high';
          }

          const secondaryImage = matchingTrigger.querySelector('img');
          if (secondaryImage) {
            secondaryImage.loading = 'lazy';
            secondaryImage.removeAttribute('fetchpriority');
          }
        }

        updatePrimaryControls(variant);
      };

      const syncVariantGallery = (variant) => {
        const gallery = product.querySelector('[data-product-media-gallery]');
        const track = gallery?.querySelector('[data-product-media-track]');
        if (!gallery || !track) return;

        const mediaGroup = mediaGroups.get(resolvedMediaIdForVariant(variant));
        const mediaItems = Array.from(track.querySelectorAll('.product__media-item'));
        const primaryMedia = product.querySelector('.product__media-primary');
        const shouldFilter = mediaAnchors.length > 1 && mediaGroup?.size > 0;

        mediaItems.forEach((item) => {
          if (item === primaryMedia) {
            item.hidden = false;
            return;
          }

          const trigger = item.querySelector('[data-product-media-open]');
          item.hidden = shouldFilter && trigger ? !mediaGroup.has(trigger.dataset.productMediaId) : false;
        });

        const orderedMediaIds = mediaIdsForVariant(variant);
        const primaryMediaId = primaryMedia?.querySelector('[data-product-media-open]')?.dataset.productMediaId;
        const primaryIndex = orderedMediaIds.indexOf(primaryMediaId);
        const circularMediaIds =
          primaryIndex >= 0
            ? [...orderedMediaIds.slice(primaryIndex), ...orderedMediaIds.slice(0, primaryIndex)]
            : orderedMediaIds;
        const mediaItemsById = new Map(
          mediaItems
            .filter((item) => item !== primaryMedia)
            .map((item) => [item.querySelector('[data-product-media-open]')?.dataset.productMediaId, item])
            .filter(([mediaId]) => Boolean(mediaId))
        );

        circularMediaIds.forEach((mediaId) => {
          const item = mediaItemsById.get(mediaId);
          if (item && !item.hidden) track.append(item);
        });

        const visibleSecondaryItems = mediaItems.filter((item) => item !== primaryMedia && !item.hidden);
        gallery.hidden = visibleSecondaryItems.length === 0 && !track.contains(primaryMedia);
        gallery.dispatchEvent(new CustomEvent('product:media-filter'));
      };

      const syncPicker = () => {
        selectedVariant = variants.find((variant) => variant.id === select.value) || selectedVariant;
        selectedValues = [...selectedVariant.values];

        groups.forEach(({ buttons, optionIndex, selectedValueLabel }) => {
          if (selectedValueLabel) selectedValueLabel.textContent = selectedValues[optionIndex];

          buttons.forEach((button) => {
            const optionValue = button.dataset.optionValue;
            const isSelected = selectedValues[optionIndex] === optionValue;
            const isAvailable = Boolean(availableVariantFor(optionIndex, optionValue));
            button.disabled = !isAvailable;
            button.setAttribute('aria-checked', String(isSelected));
            button.setAttribute('aria-label', isAvailable ? optionValue : `${optionValue} — Unavailable`);
            if (isAvailable) {
              button.removeAttribute('aria-disabled');
            } else {
              button.setAttribute('aria-disabled', 'true');
            }
            button.tabIndex = isSelected && isAvailable ? 0 : -1;
          });

          if (!buttons.some((button) => button.tabIndex === 0)) {
            const firstAvailable = buttons.find((button) => !button.disabled);
            if (firstAvailable) firstAvailable.tabIndex = 0;
          }
        });

        const price = product.querySelector('.product__price');
        if (price && selectedVariant.price) price.textContent = selectedVariant.price;
        syncVariantMedia(selectedVariant);
        syncVariantGallery(selectedVariant);
      };

      const movePrimaryMedia = (direction) => {
        const mediaIds = mediaIdsForVariant(selectedVariant);
        if (mediaIds.length < 2) return;

        currentMediaIndex = (currentMediaIndex + direction + mediaIds.length) % mediaIds.length;
        syncVariantMedia(selectedVariant);
        syncVariantGallery(selectedVariant);
      };

      primaryPrevious?.addEventListener('click', () => movePrimaryMedia(-1));
      primaryNext?.addEventListener('click', () => movePrimaryMedia(1));

      if (mediaTrack) {
        let mobileScrollFrame;
        const rememberMobileMediaIndex = () => {
          mobileScrollFrame = undefined;
          if (!mobileQuery.matches) return;

          const mediaIds = mediaIdsForVariant(selectedVariant);
          const visibleItems = Array.from(mediaTrack.querySelectorAll('.product__media-item:not([hidden])'));
          if (mediaIds.length === 0 || visibleItems.length === 0) return;

          const trackCenter = mediaTrack.scrollLeft + mediaTrack.clientWidth / 2;
          const closestItem = visibleItems.reduce((closest, item) => {
            const itemCenter = item.offsetLeft + item.offsetWidth / 2;
            const distance = Math.abs(itemCenter - trackCenter);
            return !closest || distance < closest.distance ? { distance, item } : closest;
          }, null)?.item;
          const mediaId = closestItem?.querySelector('[data-product-media-open]')?.dataset.productMediaId;
          const nextMediaIndex = mediaIds.indexOf(mediaId);
          if (nextMediaIndex >= 0) currentMediaIndex = nextMediaIndex;
        };

        mediaTrack.addEventListener(
          'scroll',
          () => {
            if (mobileScrollFrame) return;
            mobileScrollFrame = requestAnimationFrame(rememberMobileMediaIndex);
          },
          { passive: true }
        );
      }

      const syncResponsiveGallery = () => requestAnimationFrame(() => syncVariantGallery(selectedVariant));
      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', syncResponsiveGallery);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(syncResponsiveGallery);
      }

      const selectOption = (button) => {
        if (button.disabled) return;
        const optionIndex = Number.parseInt(button.dataset.optionIndex, 10);
        const optionValue = button.dataset.optionValue;
        const proposedValues = [...selectedValues];
        proposedValues[optionIndex] = optionValue;

        let nextVariant = variants.find(
          (variant) =>
            variant.available && variant.values.every((value, index) => value === proposedValues[index])
        );

        if (!nextVariant) nextVariant = availableVariantFor(optionIndex, optionValue);
        if (!nextVariant) return;

        select.value = nextVariant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        const url = new URL(window.location.href);
        url.searchParams.set('variant', nextVariant.id);
        window.history.replaceState({}, '', url);
      };

      groups.forEach(({ buttons }) => {
        buttons.forEach((button) => {
          button.addEventListener('click', () => selectOption(button));
          button.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;

            const available = buttons.filter((candidate) => !candidate.disabled);
            if (available.length === 0) return;
            event.preventDefault();

            const currentIndex = Math.max(0, available.indexOf(button));
            let nextIndex = currentIndex;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = available.length - 1;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + available.length) % available.length;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % available.length;

            available[nextIndex].focus();
            selectOption(available[nextIndex]);
          });
        });
      });

      select.before(picker);
      select.addEventListener('change', syncPicker);
      syncPicker();
    });
  };

  initializeMobileMediaFlow();
  initializeGallery();
  initializeLightbox();
  initializeVariantPickers();
})();
